import React, { useEffect, useState } from 'react';
import { Layout, Menu, theme, Button, Drawer, Typography, Space, Divider } from 'antd';
import { MenuOutlined, GiftOutlined, UnorderedListOutlined } from '@ant-design/icons';
import './MenuLayouts.css';
import { Link, useLocation } from 'react-router-dom';
import Profile from '../Auth0/Profile';

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

interface MyLayoutProps {
    pageTitle: string;
    children: React.ReactNode;
}

const MenuLayout: React.FC<MyLayoutProps> = ({ pageTitle, children }) => {
    const [selectedKey, setSelectedKey] = useState<string>('1');
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const location = useLocation();

    const {
        token: { colorBgContainer },
    } = theme.useToken();

    useEffect(() => {
        // Update selected key based on current path
        switch (location.pathname) {
            case '/sorteggio':
            case '/':
                setSelectedKey('1');
                break;
            case '/sorteggi':
                setSelectedKey('2');
                break;
            default:
                if (location.pathname.startsWith('/sorteggio/')) {
                    setSelectedKey('1');
                } else {
                    setSelectedKey('1');
                }
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const menuItems = [
        {
            key: '1',
            icon: <GiftOutlined style={{ fontSize: '16px' }} />,
            label: (
                <Link to="/sorteggio" style={{ fontWeight: 500 }}>Nuovo Sorteggio</Link>
            ),
        },
        {
            key: '2',
            icon: <UnorderedListOutlined style={{ fontSize: '16px' }} />,
            label: (
                <Link to="/sorteggi" style={{ fontWeight: 500 }}>I Miei Sorteggi</Link>
            ),
        }
    ];

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            <Header
                className="menu-header festive-header"
                style={{
                    padding: isMobile ? '0 12px' : '0 24px',
                    background: '#d32f2f',
                    borderBottom: '4px solid #b71c1c',
                    display: 'flex',
                    alignItems: 'center',
                    height: isMobile ? '64px' : '72px',
                    boxShadow: '0 4px 12px rgba(211,47,47,0.3)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000
                }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px',
                    minWidth: isMobile ? 'auto' : '200px'
                }}>
                    {isMobile ? (
                        <Button
                            icon={<MenuOutlined style={{ fontSize: '20px' }} />}
                            onClick={() => setDrawerVisible(!drawerVisible)}
                            className="menu-toggle-button"
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                color: 'white',
                                backdropFilter: 'blur(10px)'
                            }}
                            size="large"
                        />
                    ) : (
                        <>
                            <GiftOutlined style={{ fontSize: '24px', color: 'white' }} />
                            <Text strong style={{
                                color: 'white',
                                fontSize: '18px',
                                letterSpacing: '0.5px'
                            }}>
                                Secret Santa
                            </Text>
                        </>
                    )}
                </div>

                {!isMobile && (
                    <Menu
                        theme="dark"
                        mode="horizontal"
                        selectedKeys={[selectedKey]}
                        items={menuItems}
                        className="menu-horizontal"
                        style={{
                            flex: 1,
                            marginLeft: '32px',
                            background: 'transparent',
                            borderBottom: 'none',
                            fontSize: '15px'
                        }}
                    />
                )}

                <div style={{
                    flex: 1,
                    fontWeight: 600,
                    fontSize: isMobile ? '15px' : '17px',
                    color: 'white',
                    textAlign: isMobile ? 'left' : 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginLeft: isMobile ? '8px' : '24px',
                    letterSpacing: '0.3px'
                }}>
                    {isMobile && <GiftOutlined style={{ marginRight: '6px' }} />}
                    {pageTitle}
                </div>
            </Header>

            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <GiftOutlined style={{ fontSize: '24px', color: '#d32f2f' }} />
                        <span style={{ fontSize: '18px', fontWeight: 600, color: '#333' }}>Secret Santa</span>
                    </div>
                }
                placement="left"
                onClose={() => setDrawerVisible(false)}
                visible={drawerVisible}
                bodyStyle={{ padding: 0, background: '#fafafa' }}
                headerStyle={{
                    background: '#ffffff',
                    borderBottom: '3px solid #d32f2f'
                }}
            >
                <Menu
                    theme="light"
                    selectedKeys={[selectedKey]}
                    mode="inline"
                    items={menuItems}
                    onClick={() => setDrawerVisible(false)}
                    style={{ border: 'none', fontSize: '15px' }}
                />
            </Drawer>

            <Content style={{
                padding: 0,
                background: '#f5f5f5',
                minHeight: 'calc(100vh - 136px)'
            }}>
                {children}
            </Content>

            <Footer
                className="footer-layout"
                style={{
                    textAlign: 'center',
                    padding: isMobile ? '16px 12px' : '24px',
                    background: '#ffffff',
                    borderTop: '3px solid #d32f2f'
                }}
            >
                <div className="user-box">
                    <Profile />
                </div>
                <Divider style={{ margin: '16px 0' }} />
                <Space size="middle" direction={isMobile ? 'vertical' : 'horizontal'} style={{ marginBottom: '8px' }}>
                    <Link to="/privacy" style={{ color: '#666', fontSize: '12px' }}>
                        Privacy Policy
                    </Link>
                    <Link to="/terms" style={{ color: '#666', fontSize: '12px' }}>
                        Termini di Servizio
                    </Link>
                    <Link to="/cookies" style={{ color: '#666', fontSize: '12px' }}>
                        Cookie Policy
                    </Link>
                </Space>
                <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#999'
                }}>
                    © 2025 babbonatalesegreto.icu
                </div>
                <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: '#aaa'
                }}>
                    Powered by{' '}
                    <a
                        href="https://marchiosoft.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#d32f2f',
                            textDecoration: 'none',
                            fontWeight: 500
                        }}
                    >
                        MarchioSoft
                    </a>
                </div>
            </Footer>
        </Layout>
    );
};

export default MenuLayout;
